import type { LayoutOptions, Position } from 'cytoscape';
import cytoscape from 'cytoscape';
import fcose from 'cytoscape-fcose';
import { withSeededRandom } from './architectureSeed.js';
import { select } from 'd3';
import type { DrawDefinition, SVG } from '../../diagram-api/types.js';
import type { Diagram } from '../../Diagram.js';
import { log } from '../../logger.js';
import { registerIconPacks } from '../../rendering-util/icons.js';
import { selectSvgElement } from '../../rendering-util/selectSvgElement.js';
import { setupGraphViewbox } from '../../setupGraphViewbox.js';
import type { ArchitectureDB } from './architectureDb.js';
import { architectureIcons } from './architectureIcons.js';
import type {
  ArchitectureAlignment,
  ArchitectureDataStructures,
  ArchitectureGroupAlignments,
  ArchitectureJunction,
  ArchitectureLayoutHint,
  ArchitectureSpatialMap,
  EdgeSingular,
  EdgeSingularData,
  NodeSingularData,
} from './architectureTypes.js';
import {
  type ArchitectureDirection,
  type ArchitectureEdge,
  type ArchitectureGroup,
  type ArchitectureService,
  ArchitectureDirectionName,
  architectureGroupAlignmentKey,
  edgeData,
  getOppositeArchitectureDirection,
  isArchitectureDirectionXY,
  isArchitectureDirectionY,
  nodeData,
} from './architectureTypes.js';
import { drawEdges, drawGroups, drawJunctions, drawServices } from './svgDraw.js';

registerIconPacks([
  {
    name: architectureIcons.prefix,
    icons: architectureIcons,
  },
]);
cytoscape.use(fcose as any);

function addServices(services: ArchitectureService[], cy: cytoscape.Core, db: ArchitectureDB) {
  services.forEach((service) => {
    cy.add({
      group: 'nodes',
      data: {
        type: 'service',
        id: service.id,
        icon: service.icon,
        label: service.title,
        parent: service.in,
        width: db.getConfigField('iconSize'),
        height: db.getConfigField('iconSize'),
      } as NodeSingularData,
      classes: 'node-service',
    });
  });
}

function addJunctions(junctions: ArchitectureJunction[], cy: cytoscape.Core, db: ArchitectureDB) {
  junctions.forEach((junction) => {
    cy.add({
      group: 'nodes',
      data: {
        type: 'junction',
        id: junction.id,
        parent: junction.in,
        width: db.getConfigField('iconSize'),
        height: db.getConfigField('iconSize'),
      } as NodeSingularData,
      classes: 'node-junction',
    });
  });
}

function positionNodes(db: ArchitectureDB, cy: cytoscape.Core) {
  cy.nodes().map((node) => {
    const data = nodeData(node);
    if (data.type === 'group') {
      return;
    }
    data.x = node.position().x;
    data.y = node.position().y;

    const nodeElem = db.getElementById(data.id);
    nodeElem.attr('transform', 'translate(' + (data.x || 0) + ',' + (data.y || 0) + ')');
  });
}

function addGroups(groups: ArchitectureGroup[], cy: cytoscape.Core) {
  groups.forEach((group) => {
    cy.add({
      group: 'nodes',
      data: {
        type: 'group',
        id: group.id,
        icon: group.icon,
        label: group.title,
        parent: group.in,
      } as NodeSingularData,
      classes: 'node-group',
    });
  });
}

function addEdges(edges: ArchitectureEdge[], cy: cytoscape.Core) {
  edges.forEach((parsedEdge) => {
    const { lhsId, rhsId, lhsInto, lhsGroup, rhsInto, lhsDir, rhsDir, rhsGroup, title } =
      parsedEdge;
    const edgeType = isArchitectureDirectionXY(parsedEdge.lhsDir, parsedEdge.rhsDir)
      ? 'segments'
      : 'straight';
    const edge: EdgeSingularData = {
      id: `${lhsId}-${rhsId}`,
      label: title,
      source: lhsId,
      sourceDir: lhsDir,
      sourceArrow: lhsInto,
      sourceGroup: lhsGroup,
      sourceEndpoint:
        lhsDir === 'L'
          ? '0 50%'
          : lhsDir === 'R'
            ? '100% 50%'
            : lhsDir === 'T'
              ? '50% 0'
              : '50% 100%',
      target: rhsId,
      targetDir: rhsDir,
      targetArrow: rhsInto,
      targetGroup: rhsGroup,
      targetEndpoint:
        rhsDir === 'L'
          ? '0 50%'
          : rhsDir === 'R'
            ? '100% 50%'
            : rhsDir === 'T'
              ? '50% 0'
              : '50% 100%',
    };
    cy.add({
      group: 'edges',
      data: edge,
      classes: edgeType,
    });
  });
}

function getAlignments(
  db: ArchitectureDB,
  spatialMaps: ArchitectureSpatialMap[],
  groupAlignments: ArchitectureGroupAlignments,
  layoutHints: ArchitectureLayoutHint[] = []
): fcose.FcoseAlignmentConstraint {
  /**
   * Flattens the alignment map so nodes in different groups will be in the same alignment array IFF their groups don't connect in a conflicting alignment
   *
   * i.e., two groups which connect horizontally should not have nodes with vertical alignments to one another
   *
   * See: #5952
   *
   * @param alignmentMap - alignment map with the outer key being the row/col # and the inner key being the group name mapped to the nodes on that axis in the group
   * @param alignmentDir - alignment direction
   * @returns flattened alignment map with an arbitrary key mapping to nodes in the same row/col
   */
  const flattenAlignments = (
    alignmentMap: Map<number, Map<string, string[]>>,
    alignmentDir: ArchitectureAlignment
  ): Map<string, string[]> => {
    // flattened is the mapping of x/y coordinate to an array of the nodes in that row/column
    const flattened = new Map<string, string[]>();
    for (const [numericDir, alignments] of alignmentMap.entries()) {
      const dir = `${numericDir}`;
      let cnt = 0;
      const arr = [...alignments.entries()]; // [group name, array of nodes within the group on axis dir]
      if (arr.length === 1) {
        // If only one group exists in the row/column, we don't need to do anything else
        flattened.set(dir, arr[0][1]);
        continue;
      }
      for (let i = 0; i < arr.length - 1; i++) {
        for (let j = i + 1; j < arr.length; j++) {
          const [aGroupId, aNodeIds] = arr[i];
          const [bGroupId, bNodeIds] = arr[j];
          // Get how the two groups are intended to align (undefined if they aren't)
          const alignment = groupAlignments.get(architectureGroupAlignmentKey(aGroupId, bGroupId));

          if (alignment === alignmentDir) {
            // If the intended alignment between the two groups is the same as the alignment we are parsing
            flattened.set(dir, [...(flattened.get(dir) ?? []), ...aNodeIds, ...bNodeIds]); // add the node ids of both groups to the axis array in flattened
          } else if (aGroupId === 'default' || bGroupId === 'default') {
            // If either of the groups are in the default space (not in a group), use the same behavior as above
            flattened.set(dir, [...(flattened.get(dir) ?? []), ...aNodeIds, ...bNodeIds]);
          } else {
            // Otherwise, the nodes in the two groups are not intended to align
            const keyA = `${dir}-${cnt++}`;
            flattened.set(keyA, aNodeIds);
            const keyB = `${dir}-${cnt++}`;
            flattened.set(keyB, bNodeIds);
          }
        }
      }
    }
    return flattened;
  };

  const alignments = spatialMaps.map((spatialMap) => {
    const horizontalAlignments = new Map<number, Map<string, string[]>>();
    const verticalAlignments = new Map<number, Map<string, string[]>>();

    // Group service ids in a map with their x and y coordinate as the key
    spatialMap.forEach(([x, y], id) => {
      const nodeGroup = db.getNode(id)?.in ?? 'default';
      const horizontalAlignment = horizontalAlignments.get(y) ?? new Map<string, string[]>();
      if (!horizontalAlignments.has(y)) {
        horizontalAlignments.set(y, horizontalAlignment);
      }

      const verticalAlignment = verticalAlignments.get(x) ?? new Map<string, string[]>();
      if (!verticalAlignments.has(x)) {
        verticalAlignments.set(x, verticalAlignment);
      }

      for (const alignment of [horizontalAlignment, verticalAlignment]) {
        const nodeList = alignment.get(nodeGroup) ?? [];
        if (!alignment.has(nodeGroup)) {
          alignment.set(nodeGroup, nodeList);
        }
        nodeList.push(id);
      }
    });

    // Merge the values of each map into a list if the inner list has at least 2 elements
    return {
      horiz: [...flattenAlignments(horizontalAlignments, 'horizontal').values()].filter(
        (arr) => arr.length > 1
      ),
      vert: [...flattenAlignments(verticalAlignments, 'vertical').values()].filter(
        (arr) => arr.length > 1
      ),
    };
  });

  // Merge the alignment lists for each spatial map into one 2d array per axis
  const [horizontalRaw, verticalRaw] = alignments.reduce(
    ([prevHoriz, prevVert], { horiz, vert }) => {
      return [
        [...prevHoriz, ...horiz],
        [...prevVert, ...vert],
      ];
    },
    [[] as string[][], [] as string[][]]
  );

  // Drop any heuristic alignment group that contains a member of a declared layout hint.
  // The user-declared hint takes precedence — keeping both produces conflicting constraints
  // that fcose cannot satisfy and crashes on (RangeError in FDLayout.calcGrid).
  const declaredMembers = new Set<string>();
  layoutHints.forEach((hint) => hint.members.forEach((m) => declaredMembers.add(m)));
  const dropOverlapping = (groups: string[][]) =>
    groups.filter((group) => !group.some((id) => declaredMembers.has(id)));

  const horizontal = dropOverlapping(horizontalRaw);
  const vertical = dropOverlapping(verticalRaw);

  // Append user-declared layout hints. `align row` shares a Y coord (horizontal axis),
  // `align column` shares an X coord (vertical axis).
  layoutHints.forEach((hint) => {
    if (hint.members.length < 2) {
      return;
    }
    if (hint.direction === 'row') {
      horizontal.push([...hint.members]);
    } else {
      vertical.push([...hint.members]);
    }
  });

  return {
    horizontal,
    vertical,
  };
}

function getRelativeConstraints(
  spatialMaps: ArchitectureSpatialMap[],
  db: ArchitectureDB,
  layoutHints: ArchitectureLayoutHint[] = []
): fcose.FcoseRelativePlacementConstraint[] {
  const relativeConstraints: fcose.FcoseRelativePlacementConstraint[] = [];

  // Emit a chain of relative placements for each declared layout hint. This is what actually
  // separates members along the axis — the alignment constraint alone only says "share a y/x"
  // but does not order the nodes.
  const iconSize = db.getConfigField('iconSize');
  const idealMult = db.getConfigField('idealEdgeLengthMultiplier');
  const hintGap = idealMult * iconSize;
  // Track every (a,b) pair that the declared hints already constrain. The heuristic BFS
  // below will skip any pair already covered to avoid double constraints that fcose cannot
  // reconcile.
  const declaredPairs = new Set<string>();
  layoutHints.forEach((hint) => {
    for (let i = 0; i < hint.members.length - 1; i++) {
      const a = hint.members[i];
      const b = hint.members[i + 1];
      declaredPairs.add(`${a}|${b}`);
      declaredPairs.add(`${b}|${a}`);
      if (hint.direction === 'row') {
        relativeConstraints.push({ left: a, right: b, gap: hintGap });
      } else {
        relativeConstraints.push({ top: a, bottom: b, gap: hintGap });
      }
    }
  });
  const posToStr = (pos: number[]) => `${pos[0]},${pos[1]}` as const;
  const strToPos = (pos: string) => pos.split(',').map((p) => parseInt(p));

  spatialMaps.forEach((spatialMap) => {
    const invSpatialMap = new Map(
      [...spatialMap.entries()].map(([key, value]) => [posToStr(value), key])
    );

    // perform BFS
    const queue = [posToStr([0, 0])];
    const visited: Record<ReturnType<typeof posToStr>, number> = {};
    const directions: Record<ArchitectureDirection, number[]> = {
      L: [-1, 0],
      R: [1, 0],
      T: [0, 1],
      B: [0, -1],
    };
    while (queue.length > 0) {
      const curr = queue.shift();
      if (curr) {
        visited[curr] = 1;
        const currId = invSpatialMap.get(curr);
        if (currId) {
          const currPos = strToPos(curr);
          Object.entries(directions).forEach(([dir, shift]) => {
            const newPos = posToStr([currPos[0] + shift[0], currPos[1] + shift[1]]);
            const newId = invSpatialMap.get(newPos);
            // If there is an adjacent service to the current one and it has not yet been visited
            if (newId && !visited[newPos]) {
              queue.push(newPos);
              // Skip the heuristic constraint if the user has already declared a relation
              // between this pair via `align row|column`. Without this, fcose receives both
              // a heuristic and a declared placement for the same pair, which can produce
              // inconsistent solutions and crash the layout (RangeError in calcGrid).
              if (declaredPairs.has(`${currId}|${newId}`)) {
                return;
              }
              // @ts-ignore cannot determine if left/right or top/bottom are paired together
              relativeConstraints.push({
                [ArchitectureDirectionName[dir as ArchitectureDirection]]: newId,
                [ArchitectureDirectionName[
                  getOppositeArchitectureDirection(dir as ArchitectureDirection)
                ]]: currId,
                gap: idealMult * iconSize,
              });
            }
          });
        }
      }
    }
  });
  return relativeConstraints;
}

function layoutArchitecture(
  services: ArchitectureService[],
  junctions: ArchitectureJunction[],
  groups: ArchitectureGroup[],
  edges: ArchitectureEdge[],
  db: ArchitectureDB,
  { spatialMaps, groupAlignments }: ArchitectureDataStructures
): Promise<cytoscape.Core> {
  return new Promise((resolve) => {
    const renderEl = select('body').append('div').attr('id', 'cy').attr('style', 'display:none');
    const cy = cytoscape({
      container: document.getElementById('cy'),
      style: [
        {
          selector: 'edge',
          style: {
            'curve-style': 'straight',
            'source-endpoint': 'data(sourceEndpoint)',
            'target-endpoint': 'data(targetEndpoint)',
          },
        },
        {
          selector: 'edge[label]',
          style: {
            label: 'data(label)',
          },
        },
        {
          selector: 'edge.segments',
          style: {
            'curve-style': 'segments',
            'segment-weights': '0',
            'segment-distances': [0.5],
            // @ts-ignore Incorrect library types
            'edge-distances': 'endpoints',
            'source-endpoint': 'data(sourceEndpoint)',
            'target-endpoint': 'data(targetEndpoint)',
          },
        },
        {
          selector: 'node',
          style: {
            // @ts-ignore Incorrect library types
            'compound-sizing-wrt-labels': 'include',
          },
        },
        {
          selector: 'node[label]',
          style: {
            'text-valign': 'bottom',
            'text-halign': 'center',
            'font-size': `${db.getConfigField('fontSize')}px`,
          },
        },
        {
          selector: '.node-service',
          style: {
            label: 'data(label)',
            width: 'data(width)',
            height: 'data(height)',
          },
        },
        {
          selector: '.node-junction',
          style: {
            width: 'data(width)',
            height: 'data(height)',
          },
        },
        {
          selector: '.node-group',
          style: {
            // @ts-ignore Incorrect library types
            padding: `${db.getConfigField('padding')}px`,
          },
        },
      ],
      layout: {
        name: 'grid',
        boundingBox: {
          x1: 0,
          x2: 100,
          y1: 0,
          y2: 100,
        },
      },
    });
    // Remove element after layout
    renderEl.remove();

    addGroups(groups, cy);
    addServices(services, cy, db);
    addJunctions(junctions, cy, db);
    addEdges(edges, cy);
    const layoutHints = db.getLayoutHints();
    // Use the spatial map to create alignment arrays for fcose, then merge in any
    // user-declared `align row|column` hints.
    const alignmentConstraint = getAlignments(db, spatialMaps, groupAlignments, layoutHints);

    // Create the relative constraints for fcose by using an inverse of the spatial map and performing BFS on it
    const relativePlacementConstraint = getRelativeConstraints(spatialMaps, db, layoutHints);

    const iconSize = db.getConfigField('iconSize');
    const sameGroupIdealLength = db.getConfigField('idealEdgeLengthMultiplier') * iconSize;
    const crossGroupIdealLength = 0.5 * iconSize;
    const sameGroupElasticity = db.getConfigField('edgeElasticity');
    // Wrap each layout.run() with withSeededRandom so fcose's internal
    // Math.random() calls produce reproducible results. See architectureSeed.ts.
    const seed = db.getConfigField('seed');

    const layout = cy.layout({
      name: 'fcose',
      quality: 'proof',
      randomize: db.getConfigField('randomize'),
      nodeSeparation: db.getConfigField('nodeSeparation'),
      numIter: db.getConfigField('numIter'),
      styleEnabled: false,
      animate: false,
      nodeDimensionsIncludeLabels: false,
      // Adjust the edge parameters if it passes through the border of a group
      // Hacky fix for: https://github.com/iVis-at-Bilkent/cytoscape.js-fcose/issues/67
      idealEdgeLength(edge: EdgeSingular) {
        const [nodeA, nodeB] = edge.connectedNodes();
        const { parent: parentA } = nodeData(nodeA);
        const { parent: parentB } = nodeData(nodeB);
        return parentA === parentB ? sameGroupIdealLength : crossGroupIdealLength;
      },
      edgeElasticity(edge: EdgeSingular) {
        const [nodeA, nodeB] = edge.connectedNodes();
        const { parent: parentA } = nodeData(nodeA);
        const { parent: parentB } = nodeData(nodeB);
        return parentA === parentB ? sameGroupElasticity : 0.001;
      },
      alignmentConstraint,
      relativePlacementConstraint,
    } as LayoutOptions);

    // Once the diagram has been generated and the service's position cords are set, adjust the XY edges to have a 90deg bend
    layout.one('layoutstop', () => {
      function getSegmentWeights(
        source: Position,
        target: Position,
        pointX: number,
        pointY: number
      ) {
        let W, D;
        const { x: sX, y: sY } = source;
        const { x: tX, y: tY } = target;

        D =
          (pointY - sY + ((sX - pointX) * (sY - tY)) / (sX - tX)) /
          Math.sqrt(1 + Math.pow((sY - tY) / (sX - tX), 2));
        W = Math.sqrt(Math.pow(pointY - sY, 2) + Math.pow(pointX - sX, 2) - Math.pow(D, 2));

        const distAB = Math.sqrt(Math.pow(tX - sX, 2) + Math.pow(tY - sY, 2));
        W = W / distAB;

        //check whether the point (pointX, pointY) is on right or left of the line src to tgt. for instance : a point C(X, Y) and line (AB).  d=(xB-xA)(yC-yA)-(yB-yA)(xC-xA). if d>0, then C is on left of the line. if d<0, it is on right. if d=0, it is on the line.
        let delta1 = (tX - sX) * (pointY - sY) - (tY - sY) * (pointX - sX);
        switch (true) {
          case delta1 >= 0:
            delta1 = 1;
            break;
          case delta1 < 0:
            delta1 = -1;
            break;
        }
        //check whether the point (pointX, pointY) is "behind" the line src to tgt
        let delta2 = (tX - sX) * (pointX - sX) + (tY - sY) * (pointY - sY);
        switch (true) {
          case delta2 >= 0:
            delta2 = 1;
            break;
          case delta2 < 0:
            delta2 = -1;
            break;
        }

        D = Math.abs(D) * delta1; //ensure that sign of D is same as sign of delta1. Hence we need to take absolute value of D and multiply by delta1
        W = W * delta2;

        return {
          distances: D,
          weights: W,
        };
      }
      cy.startBatch();
      for (const edge of Object.values(cy.edges())) {
        if (edge.data?.()) {
          const { x: sX, y: sY } = edge.source().position();
          const { x: tX, y: tY } = edge.target().position();
          if (sX !== tX && sY !== tY) {
            const sEP = edge.sourceEndpoint();
            const tEP = edge.targetEndpoint();
            const { sourceDir } = edgeData(edge);
            const [pointX, pointY] = isArchitectureDirectionY(sourceDir)
              ? [sEP.x, tEP.y]
              : [tEP.x, sEP.y];
            const { weights, distances } = getSegmentWeights(sEP, tEP, pointX, pointY);
            edge.style('segment-distances', distances);
            edge.style('segment-weights', weights);
          }
        }
      }
      cy.endBatch();
      withSeededRandom(seed, () => layout.run());
    });
    try {
      withSeededRandom(seed, () => layout.run());
    } catch (err) {
      // fcose throws a raw `RangeError: Invalid array length` from inside
      // FDLayout.calcGrid when the constraints it receives are unsatisfiable
      // (e.g. an `align row|column` chain whose member order contradicts the
      // edge directions, or two declared alignments that overlap on a node).
      // Rethrow with actionable context so users don't have to chase the
      // failure into fcose internals.
      if (err instanceof RangeError && err.message.includes('Invalid array length')) {
        throw new Error(
          'Architecture layout failed: a declared `align row|column` directive ' +
            'likely contradicts the edge directions, or two declared alignments ' +
            'overlap on a shared node. Check that the order of members in each ' +
            '`align` chain is consistent with the edges between them, and that ' +
            'no node appears in two `align` directives along the same axis.'
        );
      }
      throw err;
    }

    cy.ready((e) => {
      log.info('Ready', e);
      resolve(cy);
    });
  });
}

export const draw: DrawDefinition = async (text, id, _version, diagObj: Diagram) => {
  // TODO: Add title support for architecture diagrams

  const db = diagObj.db as ArchitectureDB;
  db.setDiagramId(id);

  const services = db.getServices();
  const junctions = db.getJunctions();
  const groups = db.getGroups();
  const edges = db.getEdges();
  const ds = db.getDataStructures();

  const svg: SVG = selectSvgElement(id);

  const edgesElem = svg.append('g');
  edgesElem.attr('class', 'architecture-edges');

  const servicesElem = svg.append('g');
  servicesElem.attr('class', 'architecture-services');

  const groupElem = svg.append('g');
  groupElem.attr('class', 'architecture-groups');

  await drawServices(db, servicesElem, services, id);
  drawJunctions(db, servicesElem, junctions, id);

  const cy = await layoutArchitecture(services, junctions, groups, edges, db, ds);

  await drawEdges(edgesElem, cy, db, id);
  await drawGroups(groupElem, cy, db, id);
  positionNodes(db, cy);

  setupGraphViewbox(undefined, svg, db.getConfigField('padding'), db.getConfigField('useMaxWidth'));
};

export const renderer = { draw };
